var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var _a;
import { createSlice } from '@reduxjs/toolkit';
import merge from 'lodash/merge';
import omit from 'lodash/omit';
import { extractBranchServiceSelectionFilters } from 'conversional-iframe-dom/src/urlparams';
var initialState = {
    external: {
        data: null,
        hostUrl: null,
        hostBookables: null,
    },
    theme: {},
    config: {},
    bookingLink: {
        data: null,
        loading: false,
        error: null,
    },
};
var deepClone = function (input) {
    // Avoid putting frozen objects from other slices/state into this slice.
    // (This is what typically causes: "Cannot assign to read only property ...")
    try {
        // @ts-ignore
        if (typeof structuredClone === 'function')
            return structuredClone(input);
    }
    catch (e) {
        // ignore
    }
    // Fallback (works for JSON-serializable payloads)
    try {
        return JSON.parse(JSON.stringify(input));
    }
    catch (e) {
        return input;
    }
};
// Snapshots of the host location. lodash `merge` never removes keys, so a host
// page that navigated to a URL without a param would keep the stale value.
// These fields must replace wholesale on every update.
var HOST_LOCATION_KEYS = ['hostUrl', 'hostQueryParams'];
var mergeExternal = function (state, payload) {
    var _a, _b;
    var next = merge({}, state.external, payload);
    HOST_LOCATION_KEYS.forEach(function (key) {
        if (payload && key in payload)
            next[key] = payload[key];
    });
    // Bookables lists replace per key; index-merging ['a','b'] with ['c'] gives ['c','b']
    var bookables = (_a = payload === null || payload === void 0 ? void 0 : payload.data) === null || _a === void 0 ? void 0 : _a.bookables;
    if (bookables && ((_b = next.data) === null || _b === void 0 ? void 0 : _b.bookables)) {
        Object.keys(bookables).forEach(function (key) {
            if (Array.isArray(bookables[key]))
                next.data.bookables[key] = bookables[key];
        });
    }
    state.external = next;
    return next;
};
var configSlice = createSlice({
    name: 'common',
    initialState: initialState,
    reducers: {
        setConfig: function (state, action) {
            var payload = deepClone(action.payload);
            merge(state, payload);
        },
        setConfigKey: function (state, action) {
            var key = action.payload.key;
            var payload = deepClone(omit(action.payload, 'key'));
            // Merge into a fresh object to avoid retaining frozen references
            // @ts-ignore
            state[key] = merge({}, state[key], payload);
        },
        setExternal: function (state, action) {
            var payload = deepClone(action.payload);
            mergeExternal(state, payload);
        },
        // Same as setExternal, but for data coming from the host page
        // (window.conversionalIntegration / window.conversionalWidget / RPC
        // setExternalData). Bookables filters set this way — or through the URL
        // query string — are kept in hostBookables so internal flows cannot
        // override them. The host clears them by sending `data.bookables = null`.
        setExternalHost: function (state, action) {
            var _a, _b;
            var payload = deepClone(action.payload);
            var bookables = (_a = payload === null || payload === void 0 ? void 0 : payload.data) === null || _a === void 0 ? void 0 : _a.bookables;
            mergeExternal(state, payload);
            if (bookables === null) {
                state.external.hostBookables = null;
                return;
            }
            var queryBookables = extractBranchServiceSelectionFilters((_b = payload === null || payload === void 0 ? void 0 : payload.hostQueryParams) === null || _b === void 0 ? void 0 : _b.content);
            var sticky = __assign(__assign(__assign({}, (state.external.hostBookables || {})), queryBookables), (bookables || {}));
            if (Object.keys(sticky).length)
                state.external.hostBookables = sticky;
        },
        mergeState: function (state, action) {
            var payload = deepClone(action.payload);
            merge(state, payload);
        },
        fetchBookingLink: {
            reducer: function (state, action) {
                // Replace object to guarantee it's writable even if a frozen object got assigned previously
                state.bookingLink = __assign(__assign({}, (state.bookingLink || { data: null, loading: false, error: null })), { loading: true, error: null });
            },
            prepare: function (bookingLinkId) {
                return {
                    payload: { bookingLinkId: bookingLinkId },
                };
            },
        },
        fetchBookingLinkSucceeded: function (state, action) {
            state.bookingLink = __assign(__assign({}, (state.bookingLink || { data: null, loading: false, error: null })), { loading: false, data: action.payload });
        },
        fetchBookingLinkFailed: function (state, action) {
            state.bookingLink = __assign(__assign({}, (state.bookingLink || { data: null, loading: false, error: null })), { loading: false, error: action.payload });
        },
    },
});
export var setConfig = (_a = configSlice.actions, _a.setConfig), setExternal = _a.setExternal, setExternalHost = _a.setExternalHost, mergeState = _a.mergeState, setConfigKey = _a.setConfigKey, fetchBookingLink = _a.fetchBookingLink, fetchBookingLinkSucceeded = _a.fetchBookingLinkSucceeded, fetchBookingLinkFailed = _a.fetchBookingLinkFailed;
export default configSlice.reducer;
